import { useFormik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import * as Yup from 'yup';
import '../scss/button.scss';
import '../scss/form.scss';
import { ReactComponent as StarfinLogo } from '../images/stashfin-logo.svg'
import { ReactComponent as UserIcon } from '../images/user-icon.svg'
import { ReactComponent as PancardIcon } from '../images/pan-card-icon.svg'
import { ReactComponent as CakeIcon } from '../images/cake-icon.svg'
import { ReactComponent as LocationIcon } from '../images/location-icon.svg'
import { ReactComponent as EnvelopeIcon } from '../images/envelope-icon.svg'
import { ReactComponent as WarningIcon } from '../images/warning-icon.svg'
//import { ReactComponent as CalendarIcon } from '../images/calendar.svg'
//import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useGetBasicDetailsMutation, useGetCustomerDetailsMutation, useUpdateBasicDetailsMutation, useUpdateTnCMutation } from '../lib/services/eligibilityApi';
import { setBasicDetails, setDetailsAvailable, setIsChecked, setPageError, setSelectedDate } from '../lib/features/eligibilitySlice'
import PageHeader from '../components/PageHeader';
import { useDispatch, useSelector } from 'react-redux';
import { differenceInYears, isValid, parse } from 'date-fns';
import CenteredPopup from '../components/popups/CenteredPopup';
import { useTapClickMutation } from '../lib/middleware/TapClickApi';
import { pushCleverTapEvent, clevertapIdentitySet } from "../lib/middleware/CtEvents";

function RegistrationForm() {
    const [getBasicDetails] = useGetBasicDetailsMutation()
    const [tapClick] = useTapClickMutation()
    const [updateTnC] = useUpdateTnCMutation()
    const [updateBasicDetails, { isLoading }] = useUpdateBasicDetailsMutation();
    const [getCustomerDetails] = useGetCustomerDetailsMutation();
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])
    const jwt = useMemo(() => queryParams.get('jwt_token'), [queryParams])
    const secret = useMemo(() => queryParams.get('secret'), [queryParams])
    const { selectedDate, isChecked, pageerror, basicDetails } = useSelector((state) => state.eligibility);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tncAccepted, setTnCAccepted] = useState(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const footerRef = useRef(null);
    const handleOpen = () => {
        setIsModalOpen(true);
    }
    const handleClose = () => {
        setIsModalOpen(false);
        dispatch(setPageError(''))
    }
    /*const navigateToLogin = () => {
        window.open('https://www.stashfin.com/login', '_blank')
    }*/
    const modalButtons = [
        { label: 'Close', onClick: handleClose }
    ]
    /*const modalLoginButton = [
        { label: 'Login', onClick: navigateToLogin }
    ]*/

    const validationSchema = Yup.object({
        fullName: Yup.string()
            .nullable()
            .test(
                "fullName-not-na",
                "Full Name is required",
                (value) => value === "N/A" || !!value
            )
            .test(
                "fullName-alphabetic",
                "Full Name must contain only alphabetic characters",
                (value) => {
                    if (!value || value === "N/A") return true;
                    return /^[A-Za-z\s]+$/.test(value);
                }
            ),

        panCardNumber: Yup.string()
            .nullable()
            .test(
                "is-not-blank",
                "PAN Card number cannot be blank",
                (value) => value === "N/A" || !!value
            )
            .test(
                "is-valid-length",
                "PAN Card number must be exactly 10 characters long",
                (value) => value === "N/A" || (value && value.length === 10)
            )
            .test(
                "no-special-characters",
                "PAN Card number must not contain special characters",
                (value) => value === "N/A" || /^[A-Za-z0-9]*$/.test(value || "")
            )
            .test(
                "valid-pan-format",
                "Invalid PAN Card format",
                (value) =>
                    value === "N/A" ||
                    /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value || "")
            ),

        dateOfBirth: Yup.string()
            .nullable()
            .test(
                "is-not-blank",
                "Date of Birth cannot be blank",
                (value) => value === "N/A" || !!value
            )
            .test(
                "valid-format",
                "Date of Birth must be in DD-MM-YYYY format",
                (value) => {
                    if (!value || value === "N/A") return true;
                    const regex = /^\d{2}-\d{2}-\d{4}$/;
                    return regex.test(value); // Validate format
                }
            )
            .test(
                "valid-date",
                "Invalid Date. Date of Birth must be in DD-MM-YYYY format",
                (value) => {
                    if (!value || value === "N/A") return true;
                    const parsedDate = parse(value, "dd-MM-yyyy", new Date());
                    return isValid(parsedDate); // Check if it's a real date
                }
            )
            .test(
                "valid-age",
                "You must be at least 18 years old",
                (value) => {
                    if (!value || value === "N/A") return true;
                    const parsedDate = parse(value, "dd-MM-yyyy", new Date());
                    if (!isValid(parsedDate)) return false;
                    return differenceInYears(new Date(), parsedDate) >= 18; // Ensure age >= 18
                }
            ),

        pinCode: Yup.string()
            .nullable()
            .test(
                "is-not-blank",
                "PIN Code cannot be blank",
                (value) => value === "N/A" || !!value
            )
            .test(
                "is-six-digits",
                "PIN Code must be exactly 6 digits long",
                (value) => !value || /^\d{3} \d{3}$/.test(value)
            )
            .test(
                "only-digits",
                "PIN Code must only contain numeric digits",
                (value) => !value || /^\d{3}( \d{3})*$/.test(value)
            )
            .test(
                "no-leading-zero",
                "PIN Code must not start with zero",
                (value) => !value || /^[1-9]/.test(value)
            ),

        emailAddress: Yup.string()
            .nullable()
            .test(
                "is-not-blank",
                "Email address cannot be blank",
                (value) => value === "N/A" || !!value
            )
            .test(
                "emailAddress-not-na",
                "Invalid email format",
                (value) => {
                    if (value === "N/A") return true;
                    if (!value) return false;
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return emailRegex.test(value);
                }
            )
    });


    const formik = useFormik({
        initialValues: {
            fullName: '',
            panCardNumber: '',
            dateOfBirth: '',
            pinCode: '',
            emailAddress: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            /*if (allDetailsAvailable) {
                localStorage.setItem("lastVisitedPage", '/complete-your-kyc')
                navigate(`/complete-your-kyc`)
            } else {*/
            try {
                const requestData = {}
                let jwt_token
                if (jwt) {
                    jwt_token = jwt
                    localStorage.setItem('jwt', jwt)
                } else {
                    jwt_token = localStorage.getItem('jwt')
                }
                let phone = localStorage.getItem('phone')
                if(!phone) {
                    const customerRes = await getCustomerDetails({ jwt_token, scrt: secret ? secret : localStorage.getItem('secret') });
                    if(customerRes.data?.success) {
                        phone = customerRes.data?.data?.phone
                        localStorage.setItem("phone", customerRes.data?.data?.phone)
                    }
                }
                const partner_unique_id = localStorage.getItem('partner_unique_id') || null
                if (values.fullName && values.fullName !== 'N/A') requestData.name = values.fullName
                if (values.panCardNumber && values.panCardNumber !== 'N/A') requestData.pan_number = values.panCardNumber.toUpperCase()
                if (selectedDate && selectedDate !== 'N/A') {
                    const [day, month, year] = selectedDate.split("-")
                    const reformatedDate = `${year}-${month}-${day}`
                    requestData.dob = reformatedDate
                }
                if (values.pinCode && values.pinCode !== 'N/A') requestData.pin_code = values.pinCode.split(' ').join('')
                if (values.emailAddress && values.emailAddress !== 'N/A') requestData.email = values.emailAddress
                if (partner_unique_id) {
                    requestData.partner_unique_id = partner_unique_id
                } else{
                    requestData.partner_unique_id = null
                }
                /*if (JSON.stringify(requestData) === '{}') {
                    localStorage.setItem("lastVisitedPage", '/credit-approval')
                    navigate(`/credit-approval`)
                } else {*/
                const scrt = secret ? secret : localStorage.getItem('secret')
                const result = await updateBasicDetails({ ...requestData, jwt_token, phone: phone.split(' ').join(''), scrt })
                if ((result.data && result.data.status === 'error') || (result && result.status === 'error')) {
                    handleOpen()
                    dispatch(setPageError(result.data.message))
                    if (result.data.message === 'Login expired! Please re-login.') {
                        localStorage.removeItem('jwt');
                        localStorage.removeItem('lastVisitedPage');
                        const phone = localStorage.getItem('phone')
                        localStorage.clear()
                        phone ? navigate(`/?phone=${phone}`) : navigate('/');
                    }
                } else if (result.data && result.data.http_code === 201) {
                    if (result.data.message === 'Login expired! Please re-login.') {
                        handleOpen()
                        dispatch(setPageError(result.data.message))
                        localStorage.removeItem('jwt');
                        localStorage.removeItem('lastVisitedPage');
                        const phone = localStorage.getItem('phone')
                        localStorage.clear()
                        phone ? navigate(`/?phone=${phone}`) : navigate('/');
                    } else {
                        if (result && result.data && result.data.redirect_url) {
                            if (result.data.utm_source) localStorage.setItem('utm_source', result.data.utm_source)
                            window.location.href = result.data.redirect_url
                        } else {
                            handleOpen()
                            dispatch(setPageError(result.data.message))
                        }
                    }
                } else if (result.error) {
                    dispatch(setPageError('Please check your internet or try again later.'))
                    handleOpen()
                } else {
                    //localStorage.setItem("lastVisitedPage", '/credit-approval')
                    //navigate(`/credit-approval`);
                    if (result && result.data) {
                        await clevertapIdentitySet({identity: result.data.customer_id})
                        const event_name = 'web_Submit_basic_details'
                        const event_property = { Source: window.localStorage.getItem('utm_source'), DOB: requestData.dob, Name: requestData.name, email_id: requestData.email, PIncode: requestData.pin_code }
                        // await updateCtUserProfile(user);
                        pushCleverTapEvent({ event_name, event_property })
                        if (result.data.customer_segmentation) localStorage.setItem('customerSegment', result.data.customer_segmentation)
                        if (result.data.redirect_url) window.location.href = result.data.redirect_url
                    }
                }
                //}
            } catch (error) {
                dispatch(setPageError('Something went wrong. Please try again later.'));
                handleOpen()
            }
            //}
            //navigate('/credit-approval')
        },
    });

    useEffect(() => {
        const getBasic = async () => {
            try {
                let jwt_token
                if (jwt) {
                    jwt_token = jwt
                    localStorage.setItem('jwt', jwt)
                } else {
                    jwt_token = localStorage.getItem('jwt')
                }
                const scrt = secret ? secret : localStorage.getItem('secret')
                const result = await getBasicDetails({ jwt_token, scrt })
                if (result.data && result.data.tnc_acceptance) {
                    setTnCAccepted(true)
                } else {
                    setTnCAccepted(false)
                }
                if (result.data && result.data.status === 'error') {
                    /*dispatch(setPageError(result.data.message))
                    handleOpen()*/
                    if (result.data.message === 'Login expired! Please re-login.') {
                        localStorage.removeItem('jwt');
                        localStorage.removeItem('lastVisitedPage');
                        const phone = localStorage.getItem('phone')
                        localStorage.clear()
                        phone ? navigate(`/?phone=${phone}`) : navigate('/');
                    }
                } else if (result.data && result.data.http_code === '201') {
                    if (result.data.message === 'Login expired! Please re-login.') {
                        localStorage.removeItem('jwt');
                        localStorage.removeItem('lastVisitedPage');
                        const phone = localStorage.getItem('phone')
                        localStorage.clear()
                        phone ? navigate(`/?phone=${phone}`) : navigate('/');
                    } /*else {
                        handleOpen()
                        dispatch(setPageError(result.data.message))
                    }*/
                } else if (result.error) {
                    //handleOpen()
                    //dispatch(setPageError('Please check your internet or try again later.'))
                } else {
                    dispatch(setBasicDetails({ ...result.data }))
                    if (result.data.utm_source) localStorage.setItem('utm_source', result.data.utm_source)
                }
            } catch (error) {
                //dispatch(setPageError('Something went wrong. Please try again later.'));
                //handleOpen()
            }
        }
        getBasic()
    }, [dispatch, getBasicDetails, navigate, jwt, secret])

    const { setFieldValue } = formik;

    useEffect(() => {
        if (basicDetails) {
            const accept_chk = basicDetails.accept_status
            if (basicDetails.customer_name) setFieldValue('fullName', basicDetails.customer_name)
            if (basicDetails.pan_number) setFieldValue('panCardNumber', basicDetails.pan_number.toUpperCase())
            if (basicDetails.dob) setFieldValue('dateOfBirth', basicDetails.dob)
            if (basicDetails.pin_code) setFieldValue('pinCode', basicDetails.pin_code)
            if (basicDetails.email) setFieldValue('emailAddress', basicDetails.email)
            if (accept_chk) dispatch(setIsChecked(accept_chk.toString() === '1' ? true : false))
        }
        if (basicDetails) {
            if (basicDetails.customer_name && basicDetails.pan_number && basicDetails.dob && basicDetails.pin_code && basicDetails.email) dispatch(setDetailsAvailable(true))
        }
    }, [basicDetails, setFieldValue, dispatch])

    useEffect(() => {
        if (isLoading) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isLoading])

    const handleUpdateTnC = async (chkd) => {
        try {
            let jwt_token
            if (jwt) {
                jwt_token = jwt
                localStorage.setItem('jwt', jwt)
            } else {
                jwt_token = localStorage.getItem('jwt')
            }
            const scrt = secret ? secret : localStorage.getItem('secret')
            const device_id = localStorage.getItem('code')
            const result = await updateTnC({ jwt_token, scrt, device_id, accept_status: chkd ? 1 : 0 })
            if ((result.data && result.data.status === 'error') || (result && result.status === 'error')) {
                handleOpen()
                dispatch(setPageError(result.data.message))
                if (result.data.message === 'Login expired! Please re-login.') {
                    localStorage.removeItem('jwt');
                    localStorage.removeItem('lastVisitedPage');
                    const phone = localStorage.getItem('phone')
                    localStorage.clear()
                    phone ? navigate(`/?phone=${phone}`) : navigate('/');
                }
            } else if (result.data && result.data.http_code === 201) {
                if (result.data.message === 'Login expired! Please re-login.') {
                    handleOpen()
                    dispatch(setPageError(result.data.message))
                    localStorage.removeItem('jwt');
                    localStorage.removeItem('lastVisitedPage');
                    const phone = localStorage.getItem('phone')
                    localStorage.clear()
                    phone ? navigate(`/?phone=${phone}`) : navigate('/');
                } else {
                    if (result && result.data && result.data.redirect_url) {
                        //window.location.href = result.data.redirect_url
                    } else {
                        handleOpen()
                        dispatch(setPageError(result.data.message))
                    }
                }
            } else if (result.error) {
                dispatch(setPageError('Please check your internet or try again later.'))
                handleOpen()
            } else {
                if (result && result.data && result.data.redirect_url) {
                    //window.location.href = result.data.redirect_url
                }
            }
        } catch (error) {
            dispatch(setPageError('Something went wrong. Please try again later.'));
            handleOpen()
        }
    }

    return (
        <div className={`external-wrapper registration-page ${isKeyboardOpen ? 'keyboard-active' : ''}`}>
            <CenteredPopup
                isOpen={isModalOpen}
                buttons={modalButtons}
                onClose={handleClose} icon={<WarningIcon />}>
                <p>{pageerror}</p>
            </CenteredPopup>
            {isLoading && <div className='loader-wrapper'>
                <div className='loader-content'>
                    <div className='loader'></div>
                    <p>Processing your details—this won’t take long!</p>
                </div>
            </div>}
            <div className='page-content'>
                <PageHeader />
                <div className='page-heading'>
                    <h2 className='page-title'>Enter details to check loan eligibility</h2>
                </div>
                <form onSubmit={formik.handleSubmit}
                    onBlur={(e) => {
                        formik.handleBlur(e);
                        setTimeout(() => {
                            if (!document.activeElement || document.activeElement.tagName !== "INPUT") {
                                setIsKeyboardOpen(false);
                            }
                        }, 100);
                    }}
                    onFocus={() => setIsKeyboardOpen(true)}>
                    <div className="input-wrapper">
                        <UserIcon className='icon' />
                        <input
                            type="text"
                            autoComplete="off"
                            name="fullName"
                            placeholder=" "
                            value={formik.values.fullName}
                            onChange={(e) => {
                                const value = e.target.value;
                                const formattedValue = value.replace(/[^A-Za-z\s]/g, "");
                                formik.setFieldValue("fullName", formattedValue);
                            }}
                            onBlur={formik.handleBlur}
                            className={`form-control has-icon`}
                            disabled={!isChecked || formik.values.fullName === "N/A"}
                        />

                        <label>Full Name (as per PAN Card)*</label>
                        {formik.errors.fullName && formik.touched.fullName && (
                            <div className="error">{formik.errors.fullName}</div>
                        )}
                    </div>

                    <div className="input-wrapper">
                        <PancardIcon className='icon' />
                        <input
                            type="text"
                            autoComplete='off'
                            name="panCardNumber"
                            minLength={10}
                            maxLength={10}
                            placeholder=" "
                            value={formik.values.panCardNumber.toUpperCase()}
                            onChange={(e) => {
                                const uppercaseValue = e.target.value.toUpperCase();
                                const formattedValue = uppercaseValue.replace(/[^A-Z0-9]/g, "").slice(0, 10);
                                formik.setFieldValue("panCardNumber", formattedValue);
                            }}
                            onBlur={formik.handleBlur}
                            className={`form-control has-icon`}
                            disabled={!isChecked || formik.values.panCardNumber === 'N/A'}
                        />
                        <label>PAN Card Number*</label>
                        {formik.errors.panCardNumber && formik.touched.panCardNumber && (
                            <div className="error">{formik.errors.panCardNumber}</div>
                        )}
                    </div>

                    <div className={`input-wrapper iconic-picker date-picker ${selectedDate ? 'active' : ''}`}>
                        <CakeIcon className='icon' />

                        <input
                            type="text"
                            autoComplete='off'
                            name="dateOfBirth"
                            minLength={10}
                            maxLength={10}
                            inputMode="numeric"
                            placeholder=" "
                            value={formik.values.dateOfBirth}
                            onChange={(e) => {
                                let value = e.target.value.replace(/[^0-9]/g, ""); // Remove non-numeric characters

                                // Format the value into DD-MM-YYYY
                                if (value.length > 2) value = `${value.slice(0, 2)}-${value.slice(2)}`;
                                if (value.length > 5) value = `${value.slice(0, 5)}-${value.slice(5, 9)}`;

                                // Ensure the value doesn't exceed 10 characters
                                value = value.slice(0, 10)
                                formik.setFieldValue("dateOfBirth", value);
                                if (/^\d{2}-\d{2}-\d{4}$/.test(value) || value === "N/A") {
                                    dispatch(setSelectedDate(value));
                                }
                            }}
                            onBlur={formik.handleBlur}
                            className={`form-control has-icon`}
                            disabled={!isChecked || formik.values.dateOfBirth === 'N/A'}
                        />
                        {/*:
                            <DatePicker
                                onBlur={formik.handleBlur}
                                className={`form-control has-icon`}
                                dateFormat="dd-MM-yyyy"
                                selected={selectedDate}
                                shouldCloseOnSelect={false}
                                //showMonthDropdown
                                //showYearDropdown
                                //dropdownMode="select"
                                open={false}
                                maxDate={subYears(new Date(), 18)}
                                onChange={(date) => {
                                    dispatch(setSelectedDate(date))
                                    formik.setFieldValue('dateOfBirth', format(date, "dd-MM-yyyy"))
                                }}
                                //onCalendarOpen={() => setIsPickerOpen(true)}
                                //onCalendarClose={() => setIsPickerOpen(false)}
                                disabled={!isChecked}
                            />*/}
                        <label>Date of Birth (DD-MM-YYYY)*</label>
                        {formik.errors.dateOfBirth && formik.touched.dateOfBirth && (
                            <div className="error">{formik.errors.dateOfBirth}</div>
                        )}
                        {/*<CalendarIcon className='right-icon' />*/}
                    </div>

                    <div className="input-wrapper">
                        <LocationIcon className='icon' />
                        <input
                            type="text"
                            autoComplete='off'
                            name="pinCode"
                            placeholder=" "
                            inputMode="numeric"
                            maxLength={7}
                            minLength={6}
                            value={formik.values.pinCode}
                            onChange={(e) => {
                                const value = e.target.value ? e.target.value.replace(/[^0-9]/g, "") : '';
                                const formattedValue = value ? value
                                    .match(/.{1,3}/g)
                                    ?.join(" ")
                                    .slice(0, 7) : '';
                                formik.setFieldValue("pinCode", formattedValue);
                            }}
                            onBlur={formik.handleBlur}
                            className={`form-control has-icon`}
                            disabled={!isChecked || formik.values.pinCode === 'N/A'}
                        />
                        <label>PIN Code*</label>
                        {formik.errors.pinCode && formik.touched.pinCode && (
                            <div className="error">{formik.errors.pinCode}</div>
                        )}
                    </div>

                    <div className="input-wrapper">
                        <EnvelopeIcon className='icon' />
                        <input
                            type="text"
                            autoComplete='off'
                            name="emailAddress"
                            placeholder=" "
                            value={formik.values.emailAddress}
                            onChange={(e) => {
                                const value = e.target.value;
                                const formattedValue = value.replace(/[^A-Za-z0-9@._-]/g, "");
                                formik.setFieldValue("emailAddress", formattedValue);
                            }}
                            onBlur={formik.handleBlur}
                            className={`form-control has-icon`}
                            disabled={!isChecked || formik.values.emailAddress === 'N/A'}
                        />
                        <label>Email ID Address*</label>
                        {formik.errors.emailAddress && formik.touched.emailAddress && (
                            <div className="error">{formik.errors.emailAddress}</div>
                        )}
                    </div>
                    {/*<div className='consent-check'><label><div className={`checkbox ${isChecked ? 'checked' : ''}`}><input type="checkbox" checked={isChecked} onChange={() => dispatch(setIsChecked(!isChecked))} /></div> <p>I give my consent for Customer Due Diligence in terms of <Link to="/">RBI Master Direction on Know Your Customer</Link></p></label></div>*/}
                    {!tncAccepted ?
                        <div className='consent-check'><label><div className={`checkbox ${isChecked ? 'checked' : ''}`}>
                            <input type="checkbox" checked={isChecked} onChange={() => {
                                handleUpdateTnC(!isChecked)
                                dispatch(setIsChecked(!isChecked))
                            }} />
                        </div>
                            <p>By continuing, I agree with the <Link to="https://stashfin.com/licence_agreement" target='_blank'>End User License Agreement</Link>, <Link to="https://stashfin.com/privacy-policies" target='_blank'>Privacy Policy</Link>, <Link to="https://stashfin.com/platform_declaration" target="_blank">Platform Declaration</Link>, <Link to="https://stashfin.com/user_consent_for_kyc" target="_blank">User Consent for KYC</Link>,  User consent for <Link to="https://stashfin.com/user_consent_for_kyc" target='_blank'>TUCIBIL</Link> and <Link to="https://stashfin.com/fpc" target='_blank'>Fair Practices Code</Link> on the Stashfin App/Website. I authorize Stashfin to access my credit report from credit bureaus.</p></label></div> : null}
                    <div className='bottom-footer p-0' ref={footerRef} style={{ bottom: `${isKeyboardOpen && isMobile ? 10 : 10}px` }}>
                        <p className='powered-by'>Powered by <StarfinLogo /></p>
                        <button type="submit" className={`btn btn-primary ${isLoading ? 'disabled' : ''}`}>Submit</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default RegistrationForm;
